/** Support → saved replies (FR/EN) and the internal knowledge base (with the published bible). */

import {
  type SupportArticle,
  type SupportMacro,
  useSupportArticlesCreate,
  useSupportArticlesList,
  useSupportArticlesPartialUpdate,
  useSupportArticlesSearchRetrieve,
  useSupportMacrosCreate,
  useSupportMacrosList,
  useSupportMacrosPartialUpdate,
} from "@agencyos/api-client";
import { Badge, Button, Checkbox, GlassCard, SelectField, Skeleton, TextAreaField, TextField } from "@agencyos/ui-web";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { fieldErrors } from "../../lib/settings";
import { CATEGORIES, useRefreshSupport, useSupportPermissions } from "./shared";

function MacroForm({ macro, onDone }: { macro?: SupportMacro; onDone: () => void }) {
  const { t } = useTranslation();
  const refresh = useRefreshSupport();
  const texts = (macro?.body ?? {}) as Record<string, string>;
  const [form, setForm] = useState({ title: macro?.title ?? "", category: macro?.category ?? "", fr: texts.fr ?? "", en: texts.en ?? "", active: macro?.is_active ?? true });
  const create = useSupportMacrosCreate({ mutation: { onSuccess: refresh } });
  const update = useSupportMacrosPartialUpdate({ mutation: { onSuccess: refresh } });
  const errors = fieldErrors(create.error ?? update.error);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const data = { title: form.title.trim(), category: form.category, body: { fr: form.fr, en: form.en }, is_active: form.active };
    if (macro) update.mutate({ id: macro.id, data }, { onSuccess: onDone });
    else create.mutate({ data }, { onSuccess: onDone });
  };
  return (
    <form className="stack" onSubmit={onSubmit} noValidate aria-label={t("support.macros.new")}>
      <div className="form-grid">
        <TextField label={t("support.macros.titleField")} value={form.title} error={errors.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <SelectField
          label={t("support.macros.category")}
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          options={[{ value: "", label: t("support.macros.anyCategory") }, ...CATEGORIES.map((c) => ({ value: c, label: t(`support.category.${c}`) }))]}
        />
      </div>
      <TextAreaField label={t("support.macros.fr")} value={form.fr} rows={4} error={errors.body} onChange={(e) => setForm({ ...form, fr: e.target.value })} />
      <TextAreaField label={t("support.macros.en")} value={form.en} rows={4} onChange={(e) => setForm({ ...form, en: e.target.value })} />
      <Checkbox label={t("support.macros.active")} checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
      <div className="row">
        <Button type="submit" disabled={!form.title.trim() || !(form.fr.trim() || form.en.trim())}>
          {t("support.macros.save")}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          {t("support.new.cancel")}
        </Button>
      </div>
    </form>
  );
}

export function MacrosTab() {
  const { t } = useTranslation();
  const can = useSupportPermissions();
  const list = useSupportMacrosList({});
  const [editing, setEditing] = useState<SupportMacro | "new" | null>(null);
  const rows = list.data?.status === 200 ? list.data.data.results : [];
  return (
    <GlassCard as="section" className="stack" aria-labelledby="support-macros-title">
      <div className="row between">
        <h2 id="support-macros-title">{t("support.tabs.macros")}</h2>
        {can("support.knowledge.write") && !editing ? <Button onClick={() => setEditing("new")}>{t("support.macros.new")}</Button> : null}
      </div>
      <p className="muted">{t("support.macros.intro")}</p>
      {editing ? <MacroForm macro={editing === "new" ? undefined : editing} onDone={() => setEditing(null)} /> : null}
      {list.isPending ? <Skeleton /> : null}
      {!list.isPending && !rows.length ? <p className="muted">{t("support.macros.empty")}</p> : null}
      <ul className="plain-list stack">
        {rows.map((row) => (
          <li key={row.id} className="row between">
            <span>
              {row.title} {row.category ? <Badge>{t(`support.category.${row.category}`)}</Badge> : null}
              {row.is_active === false ? <Badge tone="warning">—</Badge> : null}
            </span>
            {can("support.knowledge.write") ? (
              <Button size="sm" variant="ghost" onClick={() => setEditing(row)}>
                {t("support.macros.save")}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </GlassCard>
  );
}

function ArticleForm({ article, onDone }: { article?: SupportArticle; onDone: () => void }) {
  const { t } = useTranslation();
  const refresh = useRefreshSupport();
  const [form, setForm] = useState({ title: article?.title ?? "", category: article?.category ?? "", language: article?.language ?? "fr", body: article?.body ?? "", published: article?.is_published ?? true });
  const create = useSupportArticlesCreate({ mutation: { onSuccess: refresh } });
  const update = useSupportArticlesPartialUpdate({ mutation: { onSuccess: refresh } });
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const data = { title: form.title.trim(), category: form.category, language: form.language, body: form.body, is_published: form.published };
    if (article) update.mutate({ id: article.id, data }, { onSuccess: onDone });
    else create.mutate({ data }, { onSuccess: onDone });
  };
  return (
    <form className="stack" onSubmit={onSubmit} noValidate aria-label={t("support.articles.new")}>
      <div className="form-grid">
        <TextField label={t("support.articles.titleField")} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <SelectField
          label={t("support.macros.category")}
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          options={[{ value: "", label: t("support.macros.anyCategory") }, ...CATEGORIES.map((c) => ({ value: c, label: t(`support.category.${c}`) }))]}
        />
        <SelectField label={t("support.new.language")} value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} options={[{ value: "fr", label: "Français" }, { value: "en", label: "English" }]} />
      </div>
      <TextAreaField label={t("support.articles.body")} value={form.body} rows={8} onChange={(e) => setForm({ ...form, body: e.target.value })} />
      <Checkbox label={t("support.articles.published")} checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
      <div className="row">
        <Button type="submit" disabled={!form.title.trim() || !form.body.trim()}>
          {t("support.articles.save")}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          {t("support.new.cancel")}
        </Button>
      </div>
    </form>
  );
}

export function ArticlesTab() {
  const { t } = useTranslation();
  const can = useSupportPermissions();
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<SupportArticle | "new" | null>(null);
  const list = useSupportArticlesList({});
  const search = useSupportArticlesSearchRetrieve({ q: q.trim() }, { query: { enabled: q.trim().length >= 3 } });
  const found = search.data?.status === 200 ? search.data.data : undefined;
  const rows = q.trim().length >= 3 && found ? found.articles : list.data?.status === 200 ? list.data.data.results : [];
  return (
    <GlassCard as="section" className="stack" aria-labelledby="support-articles-title">
      <div className="row between">
        <h2 id="support-articles-title">{t("support.tabs.articles")}</h2>
        {can("support.knowledge.write") && !editing ? <Button onClick={() => setEditing("new")}>{t("support.articles.new")}</Button> : null}
      </div>
      <p className="muted">{t("support.articles.intro")}</p>
      {editing ? <ArticleForm article={editing === "new" ? undefined : editing} onDone={() => setEditing(null)} /> : null}
      <TextField type="search" label={t("support.articles.search")} value={q} onChange={(e) => setQ(e.target.value)} />
      {!rows.length ? <p className="muted">{t("support.articles.empty")}</p> : null}
      {rows.map((row) => (
        <details key={row.id}>
          <summary>
            {row.title} {row.category ? <Badge>{t(`support.category.${row.category}`)}</Badge> : null}
          </summary>
          <p className="support-body">{row.body}</p>
          {can("support.knowledge.write") ? (
            <Button size="sm" variant="ghost" onClick={() => setEditing(row)}>
              {t("support.articles.save")}
            </Button>
          ) : null}
        </details>
      ))}
      {found?.bible.length ? (
        <section className="stack" aria-labelledby="support-bible-title">
          <h3 id="support-bible-title">{t("support.articles.bible")}</h3>
          {found.bible.map((section, index) => {
            const row = section as { title?: string; content?: string };
            return (
              <details key={index}>
                <summary>{row.title}</summary>
                <p className="support-body">{row.content}</p>
              </details>
            );
          })}
        </section>
      ) : null}
    </GlassCard>
  );
}
